import React, { useMemo, useState } from 'react';
import { IconEye, IconX } from '@tabler/icons-react';
import { getLogoForStyle } from '../utils/styleLogos';
import TagFilter from './TagFilter';
import './StylePicker.css';

export function getStyleBaseName(templateName) {
  if (typeof templateName !== 'string') return '';
  let base = templateName;
  if (base.includes(' 3D')) {
    base = base.replace(' 3D', '').trim();
  }
  if (base.endsWith('3D')) {
    base = base.slice(0, -2).trim();
  }
  return base.trim();
}

export function groupTemplatesByStyle(templates) {
  const groups = {};
  templates.forEach(template => {
    const baseName = getStyleBaseName(template.name);
    if (!groups[baseName]) {
      groups[baseName] = {
        name: baseName,
        templates: [],
        has3D: false,
        has2D: false,
        logoUrl: null,
        exampleImageUrl: null,
        exampleImageUrl2D: null,
        exampleImageUrl3D: null,
        portraitExampleImageUrl: null,
        portraitExampleImageUrl2D: null,
        portraitExampleImageUrl3D: null,
        tags: [],
      };
    }
    const group = groups[baseName];
    group.templates.push(template);

    const is3DVariant = template.mapInputType === '3d' || template.name.includes('3D');
    if (is3DVariant) {
      group.has3D = true;
      if (template.exampleImageUrl && !group.exampleImageUrl3D) {
        group.exampleImageUrl3D = template.exampleImageUrl;
      }
      if (template.portraitExampleImageUrl && !group.portraitExampleImageUrl3D) {
        group.portraitExampleImageUrl3D = template.portraitExampleImageUrl;
      }
    } else {
      group.has2D = true;
      if (template.exampleImageUrl && !group.exampleImageUrl2D) {
        group.exampleImageUrl2D = template.exampleImageUrl;
      }
      if (template.portraitExampleImageUrl && !group.portraitExampleImageUrl2D) {
        group.portraitExampleImageUrl2D = template.portraitExampleImageUrl;
      }
    }

    if (template.logoUrl && (!group.logoUrl || !is3DVariant)) {
      group.logoUrl = template.logoUrl;
    }
    if (template.exampleImageUrl && (!group.exampleImageUrl || !is3DVariant)) {
      group.exampleImageUrl = template.exampleImageUrl;
    }
    if (template.portraitExampleImageUrl && (!group.portraitExampleImageUrl || !is3DVariant)) {
      group.portraitExampleImageUrl = template.portraitExampleImageUrl;
    }
    if (Array.isArray(template.tags)) {
      template.tags.forEach(tag => {
        if (!group.tags.includes(tag)) group.tags.push(tag);
      });
    }
  });
  return Object.values(groups);
}

export function pickTemplateForDimension(styleGroup, use3DMap) {
  if (!styleGroup) return null;
  const want3D = use3DMap === true;
  const exactMatch = styleGroup.templates.find(t => {
    const is3D = t.mapInputType === '3d' || t.name.includes('3D');
    return is3D === want3D;
  });
  if (exactMatch) return exactMatch;
  return styleGroup.templates[0] || null;
}

const StyleCard = ({ group, isIncluded, isDisabled, onToggle, onOpenPreview }) => {
  const logoSrc = getLogoForStyle(group.name, group.logoUrl);
  const hasAnyExample = Boolean(group.exampleImageUrl2D || group.exampleImageUrl3D || group.exampleImageUrl);

  const handlePreviewClick = (e) => {
    e.stopPropagation();
    onOpenPreview?.();
  };

  return (
    <button
      type="button"
      className={`style-picker-card ${isIncluded ? 'included' : 'excluded'}`}
      onClick={() => !isDisabled && onToggle?.()}
      disabled={isDisabled}
      aria-pressed={isIncluded}
    >
      <div className="style-picker-logo">
        {logoSrc ? (
          <img src={logoSrc} alt={group.name} />
        ) : (
          <div className="style-picker-logo-fallback">🗺️</div>
        )}
      </div>
      <span className="style-picker-name">{group.name}</span>

      {isIncluded && <div className="style-picker-check">✓</div>}

      {hasAnyExample && (
        <span
          className="style-picker-preview-btn"
          onClick={handlePreviewClick}
          role="button"
          aria-label={`Preview ${group.name}`}
        >
          <IconEye size={14} />
        </span>
      )}
    </button>
  );
};

const PreviewModal = ({ group, onClose }) => {
  if (!group) return null;
  const url2D = group.exampleImageUrl2D || (!group.exampleImageUrl3D ? group.exampleImageUrl : null);
  const url3D = group.exampleImageUrl3D || null;

  return (
    <div className="style-picker-modal-overlay" onClick={onClose}>
      <div className="style-picker-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="style-picker-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          <IconX size={20} />
        </button>
        <h3 className="style-picker-modal-title">{group.name}</h3>
        <div className="style-picker-modal-images">
          {url2D && (
            <figure>
              <img src={url2D} alt={`${group.name} 2D example`} />
              <figcaption>2D</figcaption>
            </figure>
          )}
          {url3D && (
            <figure>
              <img src={url3D} alt={`${group.name} 3D example`} />
              <figcaption>3D</figcaption>
            </figure>
          )}
        </div>
      </div>
    </div>
  );
};

const StylePicker = ({
  styleGroups,
  // Legacy single-select API
  selectedStyleGroupName,
  onSelectStyleGroup,
  // Multi-select (opt-out) API
  multiSelect = false,
  excludedStyleGroupNames,
  onToggleStyleGroup,
  onSelectAll,
  onClearAll,
  // Tag filter (optional)
  availableTagCategories,
  activeTagCategory,
  activeTag,
  onChangeTagCategory,
  onChangeTag,
  disabled = false,
  heading = 'Choose Style',
  isLoading = false,
  error = null,
}) => {
  const [previewGroupName, setPreviewGroupName] = useState(null);
  const previewGroup = previewGroupName
    ? styleGroups.find(g => g.name === previewGroupName)
    : null;

  const tagCats = availableTagCategories || {};
  const hasTagFilter = Object.keys(tagCats).length > 0;

  const filteredGroups = useMemo(() => {
    if (!activeTag) return styleGroups;
    return styleGroups.filter(g => Array.isArray(g.tags) && g.tags.includes(activeTag));
  }, [styleGroups, activeTag]);

  const excludedSet = multiSelect
    ? new Set(Array.isArray(excludedStyleGroupNames) ? excludedStyleGroupNames : [])
    : null;

  return (
    <div className="style-picker">
      <div className="style-picker-header">
        {heading && <h3 className="style-picker-heading">{heading}</h3>}
        {multiSelect && !isLoading && !error && (
          <div className="style-picker-bulk-actions">
            <button
              type="button"
              className="style-picker-bulk-btn"
              onClick={() => onSelectAll?.()}
              disabled={disabled}
            >
              Select all
            </button>
            <span className="style-picker-bulk-sep">·</span>
            <button
              type="button"
              className="style-picker-bulk-btn"
              onClick={() => onClearAll?.()}
              disabled={disabled}
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {hasTagFilter && !isLoading && !error && (
        <TagFilter
          availableTagCategories={tagCats}
          activeTagCategory={activeTagCategory}
          activeTag={activeTag}
          onChangeTagCategory={onChangeTagCategory}
          onChangeTag={onChangeTag}
        />
      )}

      {error && <div className="style-picker-error">{error}</div>}
      {isLoading ? (
        <div className="style-picker-loading">Loading styles...</div>
      ) : (
        <div className="style-picker-grid">
          {filteredGroups.length === 0 ? (
            <div className="style-picker-empty">No styles match this filter.</div>
          ) : (
            filteredGroups.map(group => {
              const isIncluded = multiSelect
                ? !excludedSet.has(group.name)
                : selectedStyleGroupName === group.name;
              return (
                <StyleCard
                  key={group.name}
                  group={group}
                  isIncluded={isIncluded}
                  isDisabled={disabled}
                  onToggle={() => {
                    if (multiSelect) {
                      onToggleStyleGroup?.(group);
                    } else {
                      onSelectStyleGroup?.(group);
                    }
                  }}
                  onOpenPreview={() => setPreviewGroupName(group.name)}
                />
              );
            })
          )}
        </div>
      )}

      {previewGroup && (
        <PreviewModal group={previewGroup} onClose={() => setPreviewGroupName(null)} />
      )}
    </div>
  );
};

export default StylePicker;
